/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useEventDisplay } from "./hooks";
import { IEvent } from "../../../store/api/devices/types";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import { GridEventListener, GridRowParams, GridSortModel } from "@mui/x-data-grid";
import CircularProgress from "@mui/material/CircularProgress";
import EventsFilter from "../../../components/Nodes/EventsFilter/EventsFilter";
import { useEventsFilter } from "../../../components/Nodes/EventsFilter/hooks";
import { getSortBy } from "../../../utils/helpers";
import EventDrawer from "../../../components/Drawers/EventDrawer";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../store/hooks";
import { resetEventFilters } from "../../../store/filters/events/EventFilterReducer";
import { resetKeywordForScope } from "../../../store/search/reducer";
import { searchKeywordByScopeSelector } from "../../../store/search/selector";
import { setRegionFilterIds } from "../../../store/filters/Nodes/reducer";
import { setPaginationModel } from "../../../store/helpers/helperReducers";
import { initialState } from "../../../store/helpers/helperInitialState";
import { Button } from "../../../components/ui/button";
import { Icon } from "../../../components/Shared/Icon";
import { Loader2 } from "lucide-react";
import ActiveFilterChips from "../../../components/Shared/Filter/ActiveFilterChips";

interface EventDisplayProps {
  selectedGeoRegions?: string[];
}

const EventDisplay: React.FC<EventDisplayProps> = ({ selectedGeoRegions }) => {
  const location = useLocation();
  const dispatch = useStoreDispatch();
  const { data, isLoading, isFetching, applyingFilter, applyingPagination, eventsColumns, exportHandler, exportStatus, setSortOrderToEvent } = useEventDisplay(selectedGeoRegions);
  const { eventFilters } = useEventsFilter();
  const eventSearchKeyword = useSelector(searchKeywordByScopeSelector("events"));
  const rowsData = data?.data || [];
  const noOfRows = data?.totalItems || 0;

  const handleClearEventSearch = useCallback(() => {
    dispatch(resetKeywordForScope({ scope: "events" }));
  }, [dispatch]);

  const handleClearAllEventFilters = useCallback(() => {
    dispatch(resetEventFilters());
    dispatch(setRegionFilterIds([]));
    dispatch(resetKeywordForScope({ scope: "events" }));
  }, [dispatch]);

  const [rowClicked, setRowClicked] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<IEvent>();

  const handleCloseButtonClicked = (value: boolean) => {
    setRowClicked(value);
  };

  const handleRowClicked: GridEventListener<"rowClick"> = (params: GridRowParams<IEvent>) => {
    setRowClicked(true);
    setSelectedRowData(params.row);
  };

  // THIS USEEFFECT RESETS GLOBAL REDUX FILTERS TO INITIAL STATE WHEN NAVIGATED TO NODES/EVENTS FROM ALARMDATAGRID AND VICE-VERSA 

  useEffect(() => {
    if (location.pathname.startsWith("/nodes/events")) {
      dispatch(resetEventFilters());
      dispatch(resetKeywordForScope({ scope: "events" }));
      dispatch(setPaginationModel({ eventDisplay: initialState.pagination }));
    }
  }, [dispatch, location.pathname]);

  return (
    <div>
      {rowClicked && <EventDrawer data={selectedRowData} setRowClicked={setRowClicked} closedButtonClicked={handleCloseButtonClicked} isAlarmContext={false} />}
      <div>
        <EventsFilter />
      </div>
      <div className="flex justify-between items-end mb-1 mt-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {`Showing ${noOfRows} results`}
            {isFetching && applyingFilter && (
              <>
                <span className="text-sm" style={{ marginLeft: 16, fontStyle: 'italic', color: 'var(--muted-foreground)' }}>
                  Applying filters...
                </span>
                <CircularProgress size={12} color="inherit" style={{ marginLeft: 8 }} />
              </>
            )}
            {isFetching && applyingPagination && (
              <>
                <span className="text-sm" style={{ marginLeft: 16, fontStyle: 'italic', color: 'var(--muted-foreground)' }}>
                  Applying pagination...
                </span>
                <CircularProgress size={12} color="inherit" style={{ marginLeft: 8 }} />
              </>
            )}
          </span>
          <ActiveFilterChips
            filters={eventFilters}
            searchKeyword={eventSearchKeyword}
            onClearSearch={handleClearEventSearch}
            onClearAll={handleClearAllEventFilters}
            dispatch={dispatch}
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 relative -top-1"
          disabled={exportStatus.isLoading}
          onClick={exportHandler}
          aria-label="Export CSV"
        >
          {exportStatus.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon name="download" size={18} />}
        </Button>
      </div>
      <div>
        <CustomDataGrid
          tableId="eventDisplay"
          rows={rowsData}
          columns={eventsColumns}
          checkboxSelection={false}
          rowCount={noOfRows}
          loading={isLoading}
          disableColumnMenu={true}
          getRowId={(row: IEvent) => row.objectKey}
          onSortModelChange={(params: GridSortModel) => setSortOrderToEvent(getSortBy(params))}
          onRowClick={handleRowClicked}
          fillContainer
        />
      </div>
    </div>
  );
};

export default EventDisplay;
